/**
 * The one place that talks HTTP to the API. Services call `request` and shape what it returns; they
 * never use `fetch` themselves.
 */

/** Without `VITE_API_BASE_URL` calls go to the page's own origin, e.g. behind a reverse proxy. */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

/** A JSON call that takes longer than this is given up on. Uploads have their own rule (`upload`). */
const REQUEST_TIMEOUT_MS = 15_000

/**
 * A failed call. `code` is the API's own error code (`invalid_credentials`, `unauthenticated`...) with
 * `status` its HTTP status. When the API could not be reached or did not answer as expected, `status`
 * is 0 and `code` is `network_error`, `timeout` or `unexpected_response`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Sent as JSON. */
  body?: unknown
  /** Sent as `Authorization: Bearer <token>`. */
  token?: string
  /**
   * Cancels the call, e.g. a search that a newer one replaced. The call then rejects with an
   * `ApiError` whose code is `aborted`.
   */
  signal?: AbortSignal
}

/** The error of a call that its caller cancelled. */
const aborted = () => new ApiError(0, 'aborted', 'The call was cancelled.')

/**
 * A signal that fires when `signal` does or after `ms`, whichever comes first, and says which one it
 * was. `dispose` lets go of the timer and the listener once the call is over.
 */
function deadline(ms: number, signal?: AbortSignal) {
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, ms)
  const follow = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', follow, { once: true })
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    dispose() {
      clearTimeout(timer)
      signal?.removeEventListener('abort', follow)
    },
  }
}

/** Narrows a parsed JSON value to an object, which is what the API's answers are. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** The JSON body of a response, or null when there is none or it is not JSON. */
async function readJson(response: Response): Promise<unknown> {
  try {
    return JSON.parse(await response.text())
  } catch {
    return null
  }
}

/** The API answers every failure with `{ "error": { "code": "...", "message": "..." } }`. */
function toApiError(status: number, payload: unknown): ApiError {
  const detail = isRecord(payload) && isRecord(payload.error) ? payload.error : null
  if (detail && typeof detail.code === 'string') {
    const message = typeof detail.message === 'string' ? detail.message : detail.code
    return new ApiError(status, detail.code, message)
  }
  return new ApiError(status, 'unexpected_response', `The API answered with status ${status}.`)
}

/**
 * Calls the API and returns the parsed JSON body, or null when the answer has none (204). Throws an
 * `ApiError` on any failure.
 */
export async function request(path: string, options: RequestOptions = {}): Promise<unknown> {
  const { method = 'GET', body, token, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  if (signal?.aborted) throw aborted()
  const limit = deadline(REQUEST_TIMEOUT_MS, signal)
  try {
    let response: Response
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: limit.signal,
      })
    } catch {
      if (limit.timedOut()) throw new ApiError(0, 'timeout', 'The API took too long to answer.')
      if (signal?.aborted) throw aborted()
      throw new ApiError(0, 'network_error', 'The API could not be reached.')
    }

    const payload = await readJson(response)
    if (!response.ok) throw toApiError(response.status, payload)
    return payload
  } catch (failure) {
    // The body is read under the same deadline, and can be cut off by it too.
    if (failure instanceof ApiError) throw failure
    if (limit.timedOut()) throw new ApiError(0, 'timeout', 'The API took too long to answer.')
    if (signal?.aborted) throw aborted()
    throw new ApiError(0, 'network_error', 'The API could not be reached.')
  } finally {
    limit.dispose()
  }
}

/** An upload gives up after this long without sending or receiving anything, never after a fixed time. */
const UPLOAD_IDLE_MS = 60_000

export interface UploadOptions {
  method?: 'POST' | 'PUT'
  /** Sent as `Authorization: Bearer <token>`. */
  token?: string
  /** Called as the body goes up, with the bytes sent so far and how many there are in all. */
  onProgress?: (sent: number, total: number) => void
  /** Cancels the upload. It then rejects with an `ApiError` whose code is `aborted`. */
  signal?: AbortSignal
}

/**
 * Sends `form` as `multipart/form-data` and returns the parsed JSON answer. It uses XMLHttpRequest,
 * because `fetch` cannot report how much of a body has gone up. It has no fixed time limit, since a
 * large file on a slow line can take many minutes: it gives up only after a minute in which nothing
 * moved, with the code `timeout`. Throws an `ApiError` on any failure.
 */
export function upload(path: string, form: FormData, options: UploadOptions = {}): Promise<unknown> {
  const { method = 'PUT', token, onProgress, signal } = options

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(aborted())
      return
    }
    const xhr = new XMLHttpRequest()
    let idle: ReturnType<typeof setTimeout> | undefined
    let failure: ApiError | null = null

    const stop = (error: ApiError) => {
      failure = error
      xhr.abort()
    }
    const cancel = () => stop(aborted())
    const restartIdle = () => {
      clearTimeout(idle)
      idle = setTimeout(
        () => stop(new ApiError(0, 'timeout', 'The upload stopped moving.')),
        UPLOAD_IDLE_MS,
      )
    }
    const finish = () => {
      clearTimeout(idle)
      signal?.removeEventListener('abort', cancel)
    }

    xhr.open(method, `${API_BASE_URL}${path}`)
    xhr.setRequestHeader('Accept', 'application/json')
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.upload.addEventListener('progress', (event) => {
      restartIdle()
      if (event.lengthComputable) onProgress?.(event.loaded, event.total)
    })
    // The answer coming back counts as movement too.
    xhr.addEventListener('progress', restartIdle)
    xhr.addEventListener('load', () => {
      finish()
      let payload: unknown = null
      try {
        payload = xhr.responseText === '' ? null : JSON.parse(xhr.responseText)
      } catch {
        payload = null
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload)
      else reject(toApiError(xhr.status, payload))
    })
    xhr.addEventListener('error', () => {
      finish()
      reject(new ApiError(0, 'network_error', 'The API could not be reached.'))
    })
    xhr.addEventListener('abort', () => {
      finish()
      reject(failure ?? aborted())
    })

    signal?.addEventListener('abort', cancel, { once: true })
    restartIdle()
    xhr.send(form)
  })
}
